import type { BrandTokens } from '../types/theme'

/** Cookie key for guest / SSR theme preference. */
export const THEME_COOKIE_KEY = 'cp-theme-preference'
/** Cookie max-age: 1 year. */
export const THEME_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365
export const COMMON_DARK_SIDEBAR_COLOR = '#242526'

export const DEFAULT_BRAND_TOKENS: BrandTokens = {
  primary: '#004eeb',
  tertiary: '#004eeb',
  background: '#ffffff',
  surface: '#f5f6f8',
  error: '#d92d20',
  fontColor: '#1d2939',
  sidebar: '#ffffff',
  border: '#eaecf0',
}

export const TOKEN_CSS_MAP: Record<keyof BrandTokens, string> = {
  primary: '--cp-primary',
  secondary: '--cp-secondary',
  tertiary: '--cp-tertiary',
  background: '--cp-background',
  surface: '--cp-surface',
  error: '--cp-error',
  fontColor: '--cp-font',
  sidebar: '--cp-sidebar',
  border: '--cp-border',
}

export const BRAND_CSS_MAP: Record<keyof BrandTokens, string> = {
  primary: '--cp-brand-primary',
  secondary: '--cp-brand-secondary',
  tertiary: '--cp-brand-tertiary',
  background: '--cp-brand-background',
  surface: '--cp-brand-surface',
  error: '--cp-brand-error',
  fontColor: '--cp-brand-font',
  sidebar: '--cp-brand-sidebar',
  border: '--cp-brand-border',
}

export const TOKEN_SCALE_NAMES: Record<keyof BrandTokens, string> = {
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'tertiary',
  background: 'background',
  surface: 'surface',
  error: 'error',
  fontColor: 'font',
  sidebar: 'sidebar',
  border: 'border',
}

/**
 * HighRise (@platform-ui/highrise) defines its own root-level scales like
 * `--primary-50 … --primary-900` that every HL component reads internally
 * (HLButton, HLCheckbox, HLRadio, HLTabs, HLCalendar, etc.). Those defaults
 * are always blue regardless of the active brand theme, which is why
 * patching per-CP component `:style` overrides never scales.
 *
 * We bridge HighRise's prefixes to our `--cp-*` scales so the entire
 * HL component tree picks up the active brand primary automatically.
 *
 * Only `primary` and `error` are bridged because those are the only two
 * prefixes HighRise consumes at component level (focus rings, borders,
 * active states, danger variants). Other tokens (`background`, `surface`,
 * `tertiary`, `sidebar`, `fontColor`) are applied directly via our own
 * `--cp-*` variables in component styles and do not need a HighRise bridge.
 */
export const HIGHRISE_BRIDGE_MAP: Partial<Record<keyof BrandTokens, string>> = {
  primary: '--primary',
  error: '--error',
}

export const DEFAULT_FONT_SCALE = {
  50: '#f3f4f6',
  100: '#f2f4f7',
  200: '#d1d5db',
  300: '#9ca3af',
  400: '#98a2b3',
  500: '#667085',
  600: '#475467',
  700: '#344054',
  800: '#1d2939',
  900: '#101828',
} as const

/**
 * Brand keys that drive `--cp-text-on-*` contrast bindings.
 * `scaleVar` points at the 500 step (button fill), not 600 — in dark mode the
 * 600 step is lightened toward white and would bias pickOnColor toward black.
 */
export const ON_COLOR_BINDINGS = [
  { key: 'primary', cssVar: '--cp-text-on-primary', scaleVar: '--cp-primary-500' },
  { key: 'secondary', cssVar: '--cp-text-on-secondary', scaleVar: '--cp-secondary-500' },
  { key: 'tertiary', cssVar: '--cp-text-on-tertiary', scaleVar: '--cp-tertiary-500' },
  { key: 'error', cssVar: '--cp-text-on-error', scaleVar: '--cp-error-500' },
  { key: 'sidebar', cssVar: '--cp-text-on-sidebar', scaleVar: '--cp-sidebar-500' },
] as const

/** WCAG AA large-text floor used for dark-mode on-brand CTA contrast. */
export const DARK_MODE_ON_COLOR_MIN_CONTRAST = 3

/**
 * Product-facing copy roles derived from the generated font scale.
 * Steps are chosen for WCAG AA normal-text contrast on canvas and surface.
 */
export const SEMANTIC_TEXT_BINDINGS = [
  { cssVar: '--cp-text-primary', fontStep: 900 },
  { cssVar: '--cp-text-secondary', fontStep: 600 },
  { cssVar: '--cp-text-muted', fontStep: 500 },
  { cssVar: '--cp-text-disabled', fontStep: 300 },
] as const
